import { Box, CircularProgress, Stack, TextField } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import SaveIcon from '@mui/icons-material/Save';
import BhpLoadingButton from "../../components/BhpLoadingButton/BhpLoadingButton";
import { useManageSmsTemplates } from "./useManageSmsTemplates";
import { useNavigate } from "react-router-dom";
import { ConfirmationDialog } from "../../components/ConfirmationDialog";
import VillageConsoleContext from "../../store/VillageConsoleContext";

const textFieldSx = {

    width: '50%'

};


export const ManageSmsTemplates: React.FC = () => {

    const defaultHelperText = "In the pseudocode above {0} represents a resident's name and {1} represents the application URL";

    const [ dialogOpen, setDialogOpen ] = useState<boolean>( false );
    const [ error, setError ] = useState<boolean>( false );
    const [ helperText, setHelperText ] = useState<string>( );
    const [ isLoading, setIsLoading ] = useState< boolean >( true );
    const [ isUpdating, setIsUpdating ] = useState<boolean>( false );
    const [ newSmsTemplate, setNewSmsTemplate ] = useState<string>( '' );
    const [ smsTemplate, setSmsTemplate ] = useState<string>( '' );
    const [ smsTemplateExample, setSmsTemplateExample ] = useState<string>( '' );
    const [ smsNewTemplateExample, setNewSmsTemplateExample ] = useState<string>( '' );
    const [ templateTouched, setTemplateTouched ] = useState<boolean>( false );

    const { getSmsTemplate, uploadSmsTemplate } = useManageSmsTemplates( );
    const { domain } = useContext( VillageConsoleContext );
    const navigate = useNavigate( );


    useEffect(( ) => { initialize( ) }, [ ] );
    useEffect(( ) => { initialize( ) }, [ domain ] );


    useEffect( () => {

        const validateSmsTemplate = ( newSmsTemplate: string ): string => {

            let validationError = '';

            if( !newSmsTemplate.includes('{0}')){

                validationError += "New SMS template does not include resident's name ({0}); ";

            }

            if( !newSmsTemplate.includes('{1}')){

                validationError += "New SMS template does not include the application URL ({1}); ";

            }

            if( newSmsTemplate === smsTemplate ){

                validationError += "New SMS template is the same as the existing one; ";

            }

            return validationError;

        };

        const timer = setTimeout(() => { 

            if (!templateTouched){
                
                return '';
                
            }

            // validate 
            const validationError: string = validateSmsTemplate( newSmsTemplate );

            if( validationError ){

                setError( true );
                setHelperText( validationError ); 
                return;

            }

            setError( false );
            setHelperText( defaultHelperText );
            setNewSmsTemplateExample( generateSmsTemplateExample( newSmsTemplate ) ); 
      
         }, 300); 
      
        return () =>  clearTimeout(timer); 

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ newSmsTemplate ]);


    const generateSmsTemplateExample = ( smsTemplate: string ): string => {

        const resident: string = 'Jane Citizen';
        const url: string = 'https://test.com/1234567890';

        return smsTemplate.replaceAll( '{0}', resident ).replaceAll( '{1}', url );

    };


    const handleCancel = ( ) => setDialogOpen( false );


    const handleOk = () => {

        setDialogOpen( false );
        submit( );

    };


    const handleTemplateChange = ( event: React.ChangeEvent<HTMLInputElement> ) => {

        setNewSmsTemplate( event.target.value );
        setTemplateTouched( true );

    };


    const initialize = async( ) => {

        setIsLoading( true );
        const smsTemplate = await getSmsTemplate( domain );
        setIsLoading( false );

        if( !smsTemplate ) {

            return;

        }

        setNewSmsTemplate( smsTemplate );
        setSmsTemplate( smsTemplate );
        setSmsTemplateExample( generateSmsTemplateExample( smsTemplate ) ); 
        setNewSmsTemplate( smsTemplate );
        setHelperText( defaultHelperText );

    };


    const submit = async( ): Promise<void> => {

        if ( error ) {
            
            return;

        }

        setIsUpdating( true );
        const successUpload = await uploadSmsTemplate( domain, newSmsTemplate );
        setIsUpdating( false );

        if( successUpload ) {

            navigate( '/' );

        }
        
    };


    return (

        <>

            {
                isLoading
                    ?
                <Box
                    alignItems="center"
                    display="flex" 
                    justifyContent="center"
                    height={ '100vh' }
                >
                    <CircularProgress size={ 40 } sx={{ color: '#e65400' }}/>
                </Box>
                    :
                <Stack direction="column" display="flex" justifyContent="center" alignItems="center" gap={ 2 } sx={{ marginTop: '30px' }}>
                    
                    <TextField 
                        disabled
                        value={ smsTemplate }
                        sx={ textFieldSx }
                        label="Existing SMS Template" 
                        variant="outlined"/>
                    
                    <TextField 
                        disabled
                        value={ smsTemplateExample }
                        sx={ textFieldSx }
                        label="Using Existing Template" 
                        variant="outlined"/>
                    
                    <TextField 
                        error={error} 
                        helperText={helperText}
                        value={ newSmsTemplate }
                        sx={ textFieldSx }
                        label="New SMS Template" 
                        onChange={ handleTemplateChange }
                        variant="outlined"/>
                    
                    <TextField 
                        disabled
                        value={ smsNewTemplateExample }
                        sx={ textFieldSx }
                        label="Using New Template" 
                        variant="outlined"/>

                    <div style={{width:'50%', display: 'flex', justifyContent: 'flex-end'}}>
                        <BhpLoadingButton
                            disabled={ error || !templateTouched }
                            loading={ isUpdating }
                            loadingPosition="start"
                            onClick={( ) => setDialogOpen( true )}
                            startIcon={<SaveIcon />}
                            variant="contained"
                        >
                            Submit
                        </BhpLoadingButton>
                    </div>

                </Stack>

            }

            <ConfirmationDialog
                text={`Please confirm that you want to override the existing ${ domain } SMS-template`}
                dialogOpen={ dialogOpen }
                handleCancel={ handleCancel }
                handleOk={ handleOk } 
            />

        </>
    
    );
    
};
